import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import {
  CalendarIcon,
  Clock,
  CheckCircle,
  Pause,
  Play,
  Trash2,
  Edit,
  Building2,
  User,
  Flag,
  Lock,
} from 'lucide-react';
import { PROJECT_STATUS } from './constants';
import { PhotoGallery } from '../PhotoGallery';

/**
 * RenovationProjectCard — one project card in the grid. Purely
 * presentational; delegates edit / delete / status change / milestone
 * management to the parent via callbacks.
 */
export const RenovationProjectCard = ({
  project,
  propertyName,
  onEdit,
  onDelete,
  onStatusChange,
  onOpenMilestones,
}) => {
  if (project._restricted) {
    return (
      <Card
        className="border-slate-100 opacity-80"
        data-testid={`renovation-card-restricted-${project.id}`}
      >
        <CardContent className="p-6 space-y-3">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="font-semibold text-slate-900 truncate">
                Renovation
              </div>
              <div className="text-xs text-slate-500 flex items-center gap-1 mt-1">
                <Building2 className="h-3 w-3" />
                {propertyName}
              </div>
            </div>
            <span className="text-xs px-2 py-1 rounded border bg-slate-50 text-slate-500 capitalize">
              {(project.status || 'planning').replace(/_/g, ' ')}
            </span>
          </div>
          <p className="text-xs text-slate-500 flex items-center gap-1.5 bg-slate-50 rounded px-2 py-1 w-fit">
            <Lock className="h-3 w-3" />
            Shared without details — ask a manager for access
          </p>
        </CardContent>
      </Card>
    );
  }
  const milestonesTotal = project.milestones?.length || 0;
  const milestonesDone = (project.milestones || []).filter(
    (m) => m.status === 'completed'
  ).length;

  const overBudget =
    (project.actual_cost || 0) > (project.estimated_budget || 0);

  return (
    <Card className="bg-white border border-slate-100 hover:shadow-md transition-shadow">
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <span
                className={`px-2 py-0.5 rounded text-xs font-medium border ${PROJECT_STATUS[project.status]?.color}`}
              >
                {PROJECT_STATUS[project.status]?.label}
              </span>
              <span className="text-xs text-slate-500">{project.project_type}</span>
            </div>
            <CardTitle className="text-lg font-semibold text-slate-900">
              {project.title}
            </CardTitle>
            <p className="text-sm text-slate-500 flex items-center gap-1 mt-1">
              <Building2 className="h-3 w-3" />
              {propertyName}
            </p>
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onEdit(project)}
              data-testid={`edit-project-${project.id}`}
            >
              <Edit className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onDelete(project.id)}
              className="text-red-600 hover:text-red-700"
              data-testid={`delete-project-${project.id}`}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Progress Bar */}
        <div>
          <div className="flex justify-between text-sm mb-1">
            <span className="text-slate-500">Progress</span>
            <span className="font-medium text-slate-900">
              {project.progress_percent || 0}%
            </span>
          </div>
          <Progress value={project.progress_percent || 0} className="h-2" />
        </div>

        {/* Budget */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-xs text-slate-500">Budget</p>
            <p className="font-semibold text-slate-900">
              ${(project.estimated_budget || 0).toLocaleString()}
            </p>
          </div>
          <div>
            <p className="text-xs text-slate-500">Spent</p>
            <p className={`font-semibold ${overBudget ? 'text-red-600' : 'text-slate-900'}`}>
              ${(project.actual_cost || 0).toLocaleString()}
            </p>
          </div>
        </div>

        {/* Dates */}
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div className="flex items-center gap-2 text-slate-600">
            <CalendarIcon className="h-4 w-4" />
            <span>Start: {project.start_date || 'TBD'}</span>
          </div>
          <div className="flex items-center gap-2 text-slate-600">
            <Clock className="h-4 w-4" />
            <span>End: {project.estimated_end_date || 'TBD'}</span>
          </div>
        </div>

        {/* Contractor */}
        {project.contractor_name && (
          <div className="flex items-center gap-2 text-sm text-slate-600 pt-2 border-t border-slate-100">
            <User className="h-4 w-4" />
            <span>{project.contractor_name}</span>
            {project.contractor_phone && (
              <span className="text-slate-400">• {project.contractor_phone}</span>
            )}
          </div>
        )}

        {/* Milestones summary */}
        <button
          type="button"
          onClick={() => onOpenMilestones(project)}
          className="w-full flex items-center justify-between rounded-md border border-slate-100 hover:border-slate-300 hover:bg-slate-50 px-3 py-2 text-sm transition-colors"
          data-testid={`open-milestones-${project.id}`}
        >
          <span className="inline-flex items-center gap-2 text-slate-700">
            <Flag className="h-4 w-4 text-slate-500" />
            {milestonesTotal === 0 ? (
              <span className="text-slate-400">No milestones yet — add some</span>
            ) : (
              <>
                <span className="font-medium">Milestones</span>
                <span className="text-slate-500">
                  · {milestonesDone}/{milestonesTotal} complete
                </span>
              </>
            )}
          </span>
          <span className="text-xs text-slate-400">Manage →</span>
        </button>

        {/* Quick Status Change */}
        <div className="flex gap-2 pt-2">
          {project.status !== 'in_progress' && project.status !== 'completed' && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => onStatusChange(project.id, 'in_progress')}
              className="flex-1"
              data-testid={`start-project-${project.id}`}
            >
              <Play className="h-3 w-3 mr-1" /> Start
            </Button>
          )}
          {project.status === 'in_progress' && (
            <>
              <Button
                size="sm"
                variant="outline"
                onClick={() => onStatusChange(project.id, 'on_hold')}
                className="flex-1"
                data-testid={`hold-project-${project.id}`}
              >
                <Pause className="h-3 w-3 mr-1" /> Hold
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => onStatusChange(project.id, 'completed')}
                className="flex-1 text-emerald-600 border-emerald-200 hover:bg-emerald-50"
                data-testid={`complete-project-${project.id}`}
              >
                <CheckCircle className="h-3 w-3 mr-1" /> Complete
              </Button>
            </>
          )}
          {project.status === 'on_hold' && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => onStatusChange(project.id, 'in_progress')}
              className="flex-1"
              data-testid={`resume-project-${project.id}`}
            >
              <Play className="h-3 w-3 mr-1" /> Resume
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default RenovationProjectCard;
